import { useRef, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { DateTime } from "luxon";
import { useQuery } from "@tanstack/react-query";
import { useSnackbar } from "notistack";

import { Button } from "../../components/Buttons/Button";
import { ModalActions } from "../../components/Modals/ModalActions";
import { getContrastTextColor } from "../../utilities/helpers/getContrastTextColor";
import { TimePicker } from "../../components/TimePicker/TimePicker";
import adjustTimeForMidnightCross from "../../utilities/helpers/adjustTimeForMidnightCross";
import useShiftTimes from "../../components/Hooks/useShiftTimes";
import { shiftDurationHours, shiftStartHour } from "../../offline/shiftBands";
import { ErrorResponse } from "../../DTOs/Common/ErrorResponse";
import { plantAjax } from "../../ajax/PlantAjax";
import { stoppagesAjax } from "../../ajax/StoppagesAjax";
import { PlantJobTypeDTO } from "../../DTOs/PlantJobTypeDTO";
import { StoppagePreset } from "../../DTOs/Stoppage/MultipleStoppages";
import { PlantDTO } from "../../DTOs/Plants/PlantDTO";
import { TripleShift } from "../../types/Shift";
import { queryClient } from "../../queryClient";
import {
  PLANT_JOB_TYPES_KEY,
  addMultipleStoppagesOffline,
  availableMachinesFromCache,
} from "../../offline/multipleStoppagesOffline";
import { isOnlineNow } from "../../offline/connectivity";

interface MultipleStoppagesModalProps {
  selectedDate: DateTime;
  selectedShift: TripleShift;
  stoppageTypeGroupOptions: {
    value: number,
    label: string,
    groupColor: string,
    stoppageType: string,
    downtimeTypeId: number
  }[];
  closeModal: () => void;
  refreshPage: () => void;
}

type FilterState = {
  stoppageTypeId: number;
  stoppageSubTypeId: number;
  timeIn: DateTime;
  timeOut?: DateTime | null;
  jobTypes?: PlantJobTypeDTO[];
}

const MultipleStoppagesModal = (
  {
    selectedDate,
    selectedShift,
    stoppageTypeGroupOptions,
    closeModal,
    refreshPage
  }: MultipleStoppagesModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const {shiftStartTime: rawStart, shiftEndTime: rawEnd} = useShiftTimes();
  // Note: the afternoon band's hours come from the configured ShiftBands rather than the literal 14 and
  // 22 that used to sit here, so re-timing or re-lengthening the afternoon shift in appsettings moves
  // these pickers with it instead of leaving them clamped to the old window.
  const afternoonStartHour = shiftStartHour("A");
  const afternoonEndHour = (afternoonStartHour + shiftDurationHours("A")) % 24;
  const shiftStartTime = selectedShift[0] === "A"
    ? DateTime.fromObject({ hour: afternoonStartHour, minute: 0 }, { zone: rawStart.zone })
    : rawStart;

  const shiftEndTime = selectedShift[0] === "A"
    ? DateTime.fromObject({ hour: afternoonEndHour, minute: 0 }, { zone: rawEnd.zone })
    : rawEnd;
  const [filterState, setFilterState] = useState<FilterState>({
    stoppageTypeId: 0,
    stoppageSubTypeId: 0,
    timeIn: shiftStartTime,
    timeOut: null,
  });

  const { data: stoppagePresets = [] } = useQuery({
    queryKey: ['stoppages', 'presets', selectedDate.toISODate(), selectedShift],
    queryFn: async () => {
      // TODO: the afternoon shift workaround.
      if (selectedShift === "AFTERNOON") return [] as StoppagePreset[];

      const res = await stoppagesAjax.getStoppagePresets(selectedDate, selectedShift);
      if (res instanceof ErrorResponse || !res) return [] as StoppagePreset[];
      return res;
    },
    enabled: selectedShift !== "AFTERNOON",
  });

  const { data: plantJobTypes = [] } = useQuery({
    queryKey: ['plants', 'jobTypes'],
    queryFn: async () => {
      const res = await plantAjax.getPlantJobTypes();
      if (res instanceof ErrorResponse) return [] as PlantJobTypeDTO[];
      return res.data;
    },
  });

  const jobTypesEnum = filterState.jobTypes && filterState.jobTypes.length > 0
    ? filterState.jobTypes.reduce((acc, jt) => acc | jt.value, 0)
    : undefined;

  const { data: plants = [] } = useQuery({
    queryKey: [
      'plants', 'multipleStoppage', selectedDate.toISODate(), selectedShift,
      filterState.timeIn?.toISO(), filterState.timeOut?.toISO() ?? null, jobTypesEnum
    ],
    queryFn: async () => {
      // Note: offline the server cannot work out which machines are available, so take the ones the
      // cached operations say are up, filtered by job type from the cached plants list. Restored on the
      // merge: offline-storage kept the import but dropped this branch, which left the modal with no
      // machines to tick offline and failed its own test.
      if (!isOnlineNow())
        return availableMachinesFromCache(selectedDate, selectedShift, jobTypesEnum);

      const res = await plantAjax.getMultipleStoppagePlants(
        selectedDate, selectedShift, filterState.timeIn!, filterState.timeOut ?? undefined, jobTypesEnum
      );
      if (!res.success || !res.data) return [] as PlantDTO[];
      return res.data;
    },
    enabled: filterState.stoppageSubTypeId !== 0
      && filterState.stoppageTypeId !== 0
      && filterState.timeIn !== undefined,
  });

  const [selection, setSelection] = useState<{ forPlants: PlantDTO[]; ids: number[] } | null>(null);
  const machineIds = selection?.forPlants === plants ? selection.ids : plants.map(p => p.id);
  const setMachineIds = (ids: number[]) => setSelection({ forPlants: plants, ids });

  const hasChosenMachines = useRef(false);

  const toggleSelection = (id: number) => {
    setMachineIds(machineIds.includes(id) ? machineIds.filter(x => x !== id) : [...machineIds, id]);
  };

  const selectionFor = (available: PlantDTO[], chosen: number[]): number[] => {
    const availableIds = available.map(machine => machine.id);
    if (!hasChosenMachines.current) return availableIds;
    return chosen.filter(id => availableIds.includes(id));
  };

  const addMultipleStoppages = async () => {
    if (filterState.stoppageSubTypeId === 0 || filterState.stoppageTypeId === 0) {
      enqueueSnackbar('Stoppage type must be selected', { variant: 'error', autoHideDuration: 3000 });
    }

    if (machineIds.length === 0) {
      enqueueSnackbar('Plants must be selected', { variant: 'error', autoHideDuration: 3000 });
    }

    setIsLoading(true);
    try {
      if (!isOnlineNow()) {
        const added = await addMultipleStoppagesOffline({
          machineIds,
          machines: plants,
          stoppageTypeId: filterState.stoppageTypeId,
          stoppageSubTypeId: filterState.stoppageSubTypeId,
          timeIn: filterState.timeIn,
          timeOut: filterState.timeOut ?? undefined,
          subType: stoppageTypeGroupOptions.find(o => o.value === filterState.stoppageSubTypeId),
          selectedDate,
          selectedShift,
        });

        enqueueSnackbar(`${added} stoppages saved offline; they will sync when you reconnect`, { variant: 'info', autoHideDuration: 5000 })
        closeModal();
        refreshPage();
        return true;
      }

      const response = await stoppagesAjax.insertMultipleStoppages({
        stoppageTypeId: filterState.stoppageTypeId,
        stoppageSubTypeId: filterState.stoppageSubTypeId,
        timeIn: filterState.timeIn,
        timeOut: filterState.timeOut ?? undefined,
        machineIds
      });

      if (!response.success && !response.data) {
        response.errors?.forEach((error, index) => {
          enqueueSnackbar(error.description, { variant: 'error', autoHideDuration: 5000 + index * 1500 })
        })

        return false;
      }

      enqueueSnackbar("Multiple stoppages added successfully", { variant: 'success', autoHideDuration: 5000 })
      closeModal();
      refreshPage();

    } catch (error) {
      let message = "Error adding multiple stoppages";

      if (error instanceof Error) {
        message = error.message;
      } else if (typeof error === "string") {
        message = error;
      }

      enqueueSnackbar(message, { variant: 'error', autoHideDuration: 5000 })
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <form className="space-y-6 text-theme-text">
      <h2 className="text-2xl pb-2 font-semibold border-b border-theme-border">Create Multiple Stoppages</h2>

      <div className="flex flex-wrap gap-2">
        {stoppagePresets.map(preset => (
          <button
            key={preset.name}
            type="button"
            className="px-3 py-1 rounded bg-theme-info text-white font-medium hover:bg-theme-info-hover transition-colors cursor-pointer"
            onClick={() => {
              setFilterState(prev => ({
                ...prev,
                timeIn: preset.timeIn,
                timeOut: preset.timeOut,
                stoppageSubTypeId: preset.stoppageSubTypeId,
                stoppageTypeId: preset.stoppageTypeId,
              }));
              setSelection(null);
            }}
          >
            {preset.name}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="font-medium mb-1 block">Stoppage Type</label>
          <Autocomplete
            size="small"
            options={stoppageTypeGroupOptions}
            value={stoppageTypeGroupOptions.find(o => o.value === filterState.stoppageSubTypeId) ?? null}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            groupBy={(option) => option.stoppageType}
            renderGroup={(params) => {
              const groupColor = stoppageTypeGroupOptions.find(o => o.stoppageType === params.group)?.groupColor;
              const contrast = getContrastTextColor(groupColor!);
              return (
                <div key={params.key}>
                  <div className="px-3 py-1 font-semibold text-sm text-white"
                    style={{ backgroundColor: groupColor, color: contrast }}
                  >
                    {params.group}
                  </div>
                  <ul className="bg-white border border-theme-border divide-y">{params.children}</ul>
                </div>
              );
            }}
            onChange={(e, newValue) => {
              setFilterState(prev => ({
                ...prev,
                stoppageSubTypeId: newValue?.value ?? 0,
                stoppageTypeId: newValue?.downtimeTypeId ?? 0
              }));
            }}
            renderInput={(params) => (
              <TextField {...params} label="Stoppage Type" fullWidth />
            )}
          />
        </div>

        <div className="flex gap-4">
          <div className="flex-1">
            <label className="block font-medium mb-1">Time In</label>
            <TimePicker
              initialDate={filterState.timeIn}
              onChange={(e) => setFilterState(prev => ({ ...prev, timeIn: e! }))}
              minValue={shiftStartTime}
              maxValue={shiftEndTime}
            />
          </div>
          <div className="flex-1">
            <label className="block font-medium mb-1">Time Out</label>
            <TimePicker
              initialDate={filterState.timeOut}
              onChange={(e) => setFilterState(prev => ({ ...prev, timeOut: e }))}
              minValue={shiftStartTime}
              maxValue={shiftEndTime}
              clearable={true}
            />
          </div>
        </div>
      </div>

      <div className="flex-1">
        <label className="block font-medium mb-1">Job Types</label>
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={plantJobTypes}
          getOptionLabel={(option) => option.label}
          value={filterState.jobTypes}
          size="small"
          classes={{ root: 'w-full' }}
          onChange={(_, newValue) => setFilterState(prev => ({ ...prev, jobTypes: newValue }))}
          renderInput={(params) => <TextField {...params} label="Job Types" />}
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="font-medium">Select Machines</label>
          <button
            type="button"
            className="text-sm text-theme-info hover:underline"
            onClick={() => {
              const allSelected = plants.every(p => machineIds.includes(p.id));
              if (allSelected) setMachineIds([]);
              else setSelection(null);
            }}
          >
            {plants.every(p => machineIds.includes(p.id)) ? "Deselect All" : "Select All"}
          </button>
        </div>

        <div className="max-h-40 overflow-y-auto border rounded p-2">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            {plants.map(item => (
              <label key={item.id} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={machineIds.includes(item.id)}
                  onChange={() => toggleSelection(item.id)}
                />
                <span>{item.plantRef}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      <ModalActions className="!mt-0 !pt-4">
        <Button type="button" variant="secondary" onClick={closeModal}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="info"
          isDefaultAction
          mnemonic="a"
          isLoading={isLoading}
          onClick={addMultipleStoppages}
        >
          Add
        </Button>
      </ModalActions>
    </form>
  );
};

export default MultipleStoppagesModal;
